<template>
  <div  class="card has-padding-20">
    <div class="card-header has-margin-bottom-20 level">
      <div class="level-left">
        <a class="is-danger is-flex" @click.prevent="addNewDangerousGood">
          <h2
            class="is-size-5 has-text-weight-bold has-margin-right-20 is-uppercase"
          >Add Dangerous Good</h2>
          <b-icon icon="library-plus"></b-icon>
          
        </a>
        <div class=" has-padding-left-20">A maximum of three (3) Dangerous Goods can be added on a vessel</div>
      </div>

      <!-- Pagination -->
      <div class="level-right has-text-weight-bold">
        <!-- Dangerous Goods -->
        <div class="level-item" v-if="numberOfDangerousGoods > 1">
          <a class="is-danger" @click.prevent="previousDangerousGood">
            <b-icon icon="arrow-left-circle-outline" size="is-medium"></b-icon>
          </a>

          <a class="is-danger has-margin-left-15" @click.prevent="nextDangerousGood">
            <b-icon icon="arrow-right-circle-outline" size="is-medium"></b-icon>
          </a>
        </div>

        <span class="has-margin-left-20">{{activeDangerousGoodIndex}} of {{numberOfDangerousGoods}}</span>
      </div>
    </div>

    <validation-observer
      ref="dangerousGoodsObserver"
      tag="div"
      v-slot="{invalid, valid, untouched, errors}"
    >
      <template v-for="(dangerousGood, index) in dangerousGoods">
        <validation-observer :key="index" :ref="`dangerousGood${index+1}Observer`" tag="div">
          <dangerous-good
            v-if="isActiveDangerousGood(index)"
            :key="index"
            :data="dangerousGood"
            :dangerous-good-index="index"
            :on-delete-dangerous-good="deleteDangerousGood"
          />
        </validation-observer>
      </template>
    </validation-observer>

    <!-- Form Buttons -->
    <div class="steps-actions has-margin-top-30">
      <div class="steps-action" v-if="isFirstDangerousGood">
        <a href="#" class="button is-danger" @click="previous">Previous</a>
      </div>
      <div class="steps-action" v-else>
        <a href="#" class="button is-danger" @click="previousDangerousGood">Previous Dangerous Good</a>
      </div>
      <div class="steps-action">
        <a href="#" class="button is-success" @click="save">Save</a>
      </div>
      <div class="steps-action" v-if="isLastDangerousGood">
        <a href="#" class="button is-primary" @click="next">Next</a>
      </div>
      <div class="steps-action" v-else>
        <a href="#" class="button is-primary" @click="nextDangerousGood">Next Dangerous Good</a>
      </div>
    </div>
  </div>
</template>

<script>
import { ValidationObserver } from "vee-validate";
import DangerousGood from "./DangerousGood";

import { SHIPPER_MUTATION_TYPES } from "@/store/mutation-types";
import { createNamespacedHelpers } from "vuex";
const { mapMutations } = createNamespacedHelpers("shippers");

export default {
  name: "DangerousGoodsDetails",

  components: {
    ValidationObserver,
    DangerousGood
  },

  props: {
    dangerousGoods: { type: Array, required: true },
    moveToNext: { type: Function },
    moveToPrevious: { type: Function },
    saveShippingRequest: { type: Function }
  },

  data() {
    return {
      activeDangerousGoodIndex: 1
    };
  },

  computed: {
    numberOfDangerousGoods() {
      return this.dangerousGoods.length;
    },

    isLastDangerousGood() {
      return this.activeDangerousGoodIndex === this.numberOfDangerousGoods;
    },

    isFirstDangerousGood() {
      return this.activeDangerousGoodIndex === 1;
    }
  },

  methods: {
    ...mapMutations({
      commitAddNewDangerousGood: SHIPPER_MUTATION_TYPES.ADD_NEW_DANGEROUS_GOOD,
      commitDeleteDangerousGood: SHIPPER_MUTATION_TYPES.DELETE_DANGEROUS_GOOD
    }),

    /**
     * Move to the previous step in the multistep form
     */
    previous: function() {
      this.moveToPrevious();
    },

    /**
     * Move to the previous step in the multistep form
     */
    next: async function() {
      if (!this.validateDangerousGoodUnNumberSelection()) {
        this.$toast.open({
          duration: 4000,
          message: "You must fill in data for the dangerous good",
          type: "is-danger"
        });

        return;
      }

      const isValid = await this.validateDangerousGood(
        this.activeDangerousGoodIndex
      );

      if (!isValid) {
        this.$toast.open({
          duration: 4000,
          position: "is-top-right",
          message:
            "Dangerous good details provided is incomplete. Please review.",
          type: "is-danger"
        });

        return;
      } else {
        this.moveToNext();
      }
    },

    save: async function() {
      const isValid = await this.validateDangerousGood(
        this.activeDangerousGoodIndex
      );

      if (!isValid) {
        return;
      } else {
        this.saveShippingRequest();
      }
    },

    /**
     * Determine if the dangerousGood with index given is the active dangerous good to be displayed
     *
     * @param number  index   The index of the dangerous good
     */
    isActiveDangerousGood: function(index) {
      index = index + 1; //accounting for 0 based array

      return index === this.activeDangerousGoodIndex;
    },

    /**
     * Validate the form for a dangerous Good
     */
    validateDangerousGood: async function(dangerousGoodIndex) {
      const observer = `dangerousGood${dangerousGoodIndex}Observer`;
      let validationCount = 0;

      for (const component of this.$refs[observer]) {
        let isValid = await component.validate();
        if (!isValid) {
          validationCount = validationCount + 1;
        }
      }

      if (validationCount > 0) {
        return false;
      }

      return true;
    },

    validateDangerousGoodUnNumberSelection: function() {
      // Ensure that at least one dangerous good is provided
      if (
        this.numberOfDangerousGoods === 1 &&
        this.dangerousGoods[0].dangerous_good_info_id === null
      ) {
        return false;
      }

      return true;
    },

    /**
     * Show a toast with a success message
     */
    displaySuccessToast: function(message, duration = 3000) {
      this.$toast.open({
        duration,
        message,
        type: "is-success"
      });
    },

    /**
     * Ensure that the deangerous good index does not overflow
     */
    checkDangerousGoodOverflow: function() {
      if (this.activeDangerousGoodIndex > this.numberOfDangerousGoods) {
        this.activeDangerousGoodIndex = this.numberOfDangerousGoods;
      }
    },

    /**
     * Add a new dangerous good
     */
    addNewDangerousGood: function() {
      if (!this.validateDangerousGoodUnNumberSelection()) {
        this.$toast.open({
          duration: 4000,
          message: "Please fill in data for the exisiting danderous good.",
          type: "is-danger"
        });

        return;
      }

      // Limit the number of dangerous goods to 3
      if (this.numberOfDangerousGoods >= 3) {
        this.$toast.open({
          duration: 4000,
          message:
            "You can have a maximum of 3 dangerous goods per shipping request",
          type: "is-danger"
        });

        return;
      }

      this.$dialog.confirm({
        title: "Add a new dangerous good",
        message:
          "Are you sure you want to <b>add</b> a new dangerous good to this shipping request?",
        confirmText: "Yes, Add",
        type: "is-primary",
        hasIcon: false,
        onConfirm: () => {
          this.commitAddNewDangerousGood();
          // Move to the last added dangerous good
          this.activeDangerousGoodIndex = this.numberOfDangerousGoods + 1;
          this.displaySuccessToast(`New dangerous good created`);
        }
      });
    },

    /**
     * Delete a dangeroous Good
     */
    deleteDangerousGood: function(dangerousGoodIndex) {
      if (this.numberOfDangerousGoods === 1) {
        this.$toast.open({
          duration: 4000,
          message:
            "You must have at least 1 dangerous good per shipping request",
          type: "is-danger"
        });

        return;
      }
      this.commitDeleteDangerousGood(dangerousGoodIndex);
      this.checkDangerousGoodOverflow();
      this.displaySuccessToast(`The Dangerous Good has been deleted`);
    },

    /**
     * Move to the previous dangerous good
     */
    previousDangerousGood: async function() {
      const isValid = await this.validateDangerousGood(
        this.activeDangerousGoodIndex
      );

      if (!isValid) return;

      if (this.activeDangerousGoodIndex - 1 < 1) {
        return;
      }

      this.activeDangerousGoodIndex = this.activeDangerousGoodIndex - 1;
    },

    /**
     * Move to the next dangerous good.
     */
    nextDangerousGood: async function() {
      const isValid = await this.validateDangerousGood(
        this.activeDangerousGoodIndex
      );

      if (!isValid) return;

      if (this.activeDangerousGoodIndex + 1 > this.numberOfDangerousGoods) {
        return;
      }

      this.activeDangerousGoodIndex = this.activeDangerousGoodIndex + 1;
    }
  }
};
</script>
